import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ScoreRing, StatusTag } from "../../../components/ui";
import axiosInstance from "../../../interceptors";
import { getInterview, getInterviewScores } from "../../../api/interview";
import RecommendationBadge from "../../../components/shared/RecommendationBadge";

const isCompleted = (s) => String(s ?? "").toUpperCase() === "COMPLETED";

const recommendationTone = (r) => {
  const v = String(r || "").toLowerCase();
  if (v.includes("no hire")) return { bg: "#FEE2E2", fg: "#B91C1C" };
  if (v.includes("strong hire") || v === "hire")
    return { bg: "#D1FAE5", fg: "#047857" };
  if (v.includes("lean")) return { bg: "#FEF3C7", fg: "#92400E" };
  return { bg: "#F1F5F9", fg: "#475569" };
};

// Inline action icons (no icon library installed).
const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const DownloadIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const STATUS_TAG = (s) => {
  if (s === "Scored") return <StatusTag type="green" text="Scored" />;
  if (s === "Live") return <StatusTag type="amber" text="Live" />;
  if (s === "Awaiting") return <StatusTag type="slate" text="Awaiting" />;
  return <StatusTag type="slate" text={s} />;
};
const initials = (n) =>
  (n || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
const avatarColor = (id) =>
  ["#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#7B5AC4"][
    (id || 0) % 6
  ];
const Interviews = ({
  rows: rowsProp,
  embedded = false,
  position,
  onClose,
} = {}) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(rowsProp ?? []);
  const [activeTab, setActiveTab] = useState("All");
  const [compareMode, setCompareMode] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  // Remind UX state — per-row "sending"/"sent" indicators.
  const [remindState, setRemindState] = useState({});

  // Per-row Download spinner state. 'downloading' while the report-url request
  // is in flight, cleared once the download/redirect fires.
  const [actionState, setActionState] = useState({});

  // Score cache keyed by interviewId. Survives tab-filter switches and embedded
  // parent re-renders. Shape: Map<id, { status: 'loading'|'ready'|'failed', score?, recommendation? }>
  const scoreCacheRef = useRef(new Map());
  // In-flight + fetched set so we don't re-fetch on every rows change.
  const scoreInFlightRef = useRef(new Set());
  // Bumped after cache writes so the table re-renders.
  const [, setScoreVersion] = useState(0);
  // Admins see all candidates; regular members (Organization_User) see only
  // candidates for jobs they created. The list is scoped server-side from the
  // JWT — this flag only tailors the copy (banner). Default true (show all) so a
  // resolution hiccup never mislabels an admin's screen.
  const [isAdmin, setIsAdmin] = useState(true);
  const sendReminder = async (row, e) => {
    e?.stopPropagation();
    const key = row.id;
    setRemindState((s) => ({ ...s, [key]: "sending" }));
    try {
      await axiosInstance.post(
        `/job-posting/job-details/interviews/${row.id}/remind`,
        { jobId: row.jobId, candidateId: row.candidateId },
      );
      setRemindState((s) => ({ ...s, [key]: "sent" }));
      setTimeout(
        () => setRemindState((s) => ({ ...s, [key]: undefined })),
        2500,
      );
    } catch {
      setRemindState((s) => ({ ...s, [key]: "failed" }));
      setTimeout(
        () => setRemindState((s) => ({ ...s, [key]: undefined })),
        2500,
      );
    }
  };

  useEffect(() => {
    if (!embedded) document.title = "Interviews - Ample Hire";
  }, [embedded]);

  // When the parent (embedded usage) supplies / updates rowsProp, mirror it.
  useEffect(() => {
    if (rowsProp) setRows(rowsProp);
  }, [rowsProp]);

  // Standalone route — fetch the full interviews list from the backend.
  useEffect(() => {
    if (embedded || rowsProp) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axiosInstance.get(
          "/job-posting/job-details/jobs/interviews",
        );
        const data = r.data?.data ?? r.data;
        // The endpoint returns an array of jobs, each with nested `interviews`.
        // Flatten while attaching jobId / position so the table shows them.
        let flat = [];
        if (
          Array.isArray(data) &&
          data.length &&
          Array.isArray(data[0]?.interviews)
        ) {
          flat = data.flatMap((j) =>
            (j.interviews || []).map((iv) => ({
              ...iv,
              jobId: iv.jobId ?? j.jobId,
              position: iv.position || j.position,
            })),
          );
        } else if (Array.isArray(data?.interviews)) {
          flat = data.interviews;
        } else if (Array.isArray(data?.list)) {
          flat = data.list;
        } else if (Array.isArray(data)) {
          flat = data;
        }
        if (!cancelled) setRows(flat);
      } catch {
        // SEED already in state — leave as-is so UI isn't blank.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [embedded, rowsProp]);

  // Resolve the caller's user type (authoritative, from master) to decide
  // admin-vs-member copy. Purely cosmetic — the candidate list is already scoped
  // server-side. Skipped when embedded (the parent screen owns the header).
  useEffect(() => {
    if (embedded) return;
    let cancelled = false;
    (async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;
        const r = await axiosInstance.post("/common/user/get-user-type", {
          id: userId,
        });
        const type = (r.data || "").toString();
        if (!cancelled) setIsAdmin(type !== "Organization_User");
      } catch {
        if (!cancelled) setIsAdmin(true); // fail open to the full (admin) view
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [embedded]);

  // Batch score fetch with cache + dedupe. One POST to /interviews/scores covers
  // every not-yet-fetched completed interview, replacing the old per-row
  // report-json fan-out. Only completed interviews have a score, so we skip every
  // other status. Failures fall back to "—" via the render path.
  useEffect(() => {
    const missing = rows.filter(
      (r) =>
        r?.id != null &&
        isCompleted(r.status) &&
        !scoreInFlightRef.current.has(r.id),
    );
    if (missing.length === 0) return;

    missing.forEach((r) => {
      scoreInFlightRef.current.add(r.id);
      scoreCacheRef.current.set(r.id, { status: "loading" });
    });
    setScoreVersion((v) => v + 1);

    const controller = new AbortController();
    let cancelled = false;

    const tenantId = localStorage.getItem("tenantId") || "1";
    const interviewIds = missing.map((r) => r.id);

    getInterviewScores(tenantId, interviewIds, { signal: controller.signal })
      .then((res) => {
        const results = res.data?.results ?? res.data?.data?.results ?? [];
        // Server may echo ids as strings ("intv_1") while rows use numbers —
        // key by String(...) on both sides so the join holds either way.
        const byId = new Map(
          results.map((x) => [String(x.interview_id), x]),
        );
        missing.forEach((r) => {
          const hit = byId.get(String(r.id));
          // found:false (or absent) → ready with null, so the row shows "—"
          // instead of spinning forever. Same for found-but-unanalysed rows.
          scoreCacheRef.current.set(r.id, {
            status: "ready",
            score: hit?.found ? (hit.overall_score ?? null) : null,
            recommendation: hit?.found ? (hit.recommendation ?? null) : null,
          });
        });
        if (!cancelled) setScoreVersion((v) => v + 1);
      })
      .catch((err) => {
        // Aborted requests can be retried later — clear the dedupe markers.
        if (err?.name === "CanceledError" || err?.name === "AbortError") {
          missing.forEach((r) => {
            scoreInFlightRef.current.delete(r.id);
            scoreCacheRef.current.delete(r.id);
          });
          return;
        }
        missing.forEach((r) =>
          scoreCacheRef.current.set(r.id, { status: "failed" }),
        );
        if (!cancelled) setScoreVersion((v) => v + 1);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [rows]);

  const openReport = (row, e) => {
    e?.stopPropagation();
    navigate(`/admin/interview-report?id=${row.id}`);
  };

  // Try to download the HTML report. Browsers honour `download` only when CORS
  // permits it; otherwise this falls back to opening the URL in a new tab,
  // which matches the previous "view" behaviour.
  const downloadReport = async (row, e) => {
    e?.stopPropagation();
    if (actionState[row.id]) return;
    setActionState((s) => ({ ...s, [row.id]: "downloading" }));
    try {
      const res = await getInterview(row.id, "report-url");
      const payload = res.data?.data ?? res.data ?? {};
      const url =
        payload?.url ?? (typeof payload === "string" ? payload : null);
      if (!url) {
        alert("No report URL returned by the server.");
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = `interview-${row.id}-report.html`;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      alert("Failed to fetch report URL. Please try again.");
    } finally {
      setActionState((s) => ({ ...s, [row.id]: null }));
    }
  };

  const counts = useMemo(
    () => ({
      All: rows.length,
      Awaiting: rows.filter((r) => r.status === "Awaiting").length,
      Live: rows.filter((r) => r.status === "Live").length,
      Scored: rows.filter((r) => r.status === "Scored").length,
    }),
    [rows],
  );

  const filtered = useMemo(
    () =>
      activeTab === "All" ? rows : rows.filter((r) => r.status === activeTab),
    [rows, activeTab],
  );

  const onRowClick = (r) => {
    if (compareMode || bulkMode) {
      setSelected((s) => {
        const n = new Set(s);
        n.has(r.id) ? n.delete(r.id) : n.add(r.id);
        return n;
      });
      return;
    }
    if (r.status === "Scored")
      navigate("/admin/candidate-report?candidateId=" + r.candidateId);
  };

  const toggleAll = () => {
    if (selected.size === filtered.length && filtered.length > 0)
      setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => r.id)));
  };

  const startCompare = () => {
    setCompareMode((v) => !v);
    setBulkMode(false);
    setSelected(new Set());
  };
  const startBulk = () => {
    setBulkMode((v) => !v);
    setCompareMode(false);
    setSelected(new Set());
  };
  const compareNow = () => {
    if (selected.size < 2) {
      alert("Pick at least 2 scored candidates to compare.");
      return;
    }
    navigate("/admin/compare?ids=" + [...selected].join(","));
  };

  const wrapperProps = embedded
    ? { style: { padding: 0 } }
    : {
        className: "app-content page-enter",
        style: { maxWidth: 1280, margin: "0 auto", padding: "20px 24px 32px" },
      };

  return (
    <div {...wrapperProps}>
      <div style={hdrRow} className="flex-col md:flex-row md:items-end">
        <div className="min-w-0">
          <h1 style={hdrTitle}>{embedded ? "Track invites" : "Interviews"}</h1>
          <p style={hdrSub}>
            {position ? `${position} · ` : ""}
            {filtered.length} session{filtered.length !== 1 ? "s" : ""} — click
            any row to view answers and AI scoring
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!embedded && compareMode && (
            <button
              onClick={compareNow}
              disabled={selected.size < 2}
              style={{ ...btnPrimary, opacity: selected.size < 2 ? 0.5 : 1 }}
            >
              Compare {selected.size} →
            </button>
          )}
          {!embedded && bulkMode && (
            <button
              onClick={() =>
                alert(`Stub: bulk action on ${selected.size} session(s)`)
              }
              disabled={selected.size === 0}
              style={{ ...btnPrimary, opacity: selected.size === 0 ? 0.5 : 1 }}
            >
              Bulk action ({selected.size})
            </button>
          )}
          {!embedded && (
            <button
              onClick={startCompare}
              style={{ ...btnGhost, ...(compareMode ? activeBtn : {}) }}
            >
              Compare mode {compareMode && "✓"}
            </button>
          )}
          {!embedded && (
            <button
              onClick={startBulk}
              style={{ ...btnGhost, ...(bulkMode ? activeBtn : {}) }}
            >
              Bulk mode {bulkMode && "✓"}
            </button>
          )}
          {embedded && onClose && (
            <button onClick={onClose} style={btnGhost} aria-label="Close">
              Close
            </button>
          )}
        </div>
      </div>

      {/* Member scope hint — only for non-admins on the standalone route
          (candidate list is scoped to jobs they created). */}
      {!embedded && !isAdmin && (
        <div
          style={{
            marginBottom: 12,
            padding: "8px 14px",
            borderRadius: 8,
            border: "1px solid #E2E8F0",
            background: "#F8FAFC",
            fontSize: 13,
            color: "#64748B",
          }}
        >
          Showing candidates for jobs you created.
        </div>
      )}

      {/* Status filter tabs — scroll horizontally on mobile so the row never wraps. */}
      <div className="flex gap-1.5 mb-3 overflow-x-auto -mx-2 px-2">
        {Object.entries(counts).map(([k, n]) => {
          const active = k === activeTab;
          return (
            <button
              key={k}
              onClick={() => setActiveTab(k)}
              style={{
                padding: "7px 14px",
                borderRadius: 100,
                fontSize: 12,
                fontWeight: 600,
                border: active ? "1px solid #7B5AC4" : "1px solid #E2E8F0",
                background: active ? "#EEF2FF" : "white",
                color: active ? "#4338CA" : "#64748B",
                cursor: "pointer",
              }}
            >
              {k} <span style={{ marginLeft: 4, opacity: 0.7 }}>({n})</span>
            </button>
          );
        })}
      </div>

      {/* Table — wide tables don't fit mobile cleanly; allow horizontal
          scroll inside the rounded card so the chrome stays intact. */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="overflow-x-auto"
        style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: 12,
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
        >
          <thead style={{ background: "#F8FAFC" }}>
            <tr>
              {(compareMode || bulkMode) && (
                <th style={{ ...th, width: 36 }}>
                  <input
                    type="checkbox"
                    checked={
                      filtered.length > 0 && selected.size === filtered.length
                    }
                    onChange={toggleAll}
                    style={{ accentColor: "#7B5AC4" }}
                  />
                </th>
              )}
              <th style={{ ...th, width: 50 }}></th>
              <th style={th}>Candidate</th>
              <th style={th}>Position</th>
              <th style={{ ...th, width: 70 }}>Score</th>
              <th style={{ ...th, width: 120 }}>Recommendation</th>
              <th style={{ ...th, width: 100 }}>Status</th>
              <th style={{ ...th, width: 110 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={compareMode || bulkMode ? 8 : 7}
                  style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}
                >
                  No interviews in this category.
                </td>
              </tr>
            )}
            {filtered.map((r) => {
              const isSelected = selected.has(r.id);
              return (
                <tr
                  key={r.id}
                  onClick={() => onRowClick(r)}
                  style={{
                    borderBottom: "1px solid #F1F5F9",
                    cursor: "pointer",
                    background: isSelected ? "#EEF2FF" : "white",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "#F8FAFC";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.background = "white";
                  }}
                >
                  {(compareMode || bulkMode) && (
                    <td style={td} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onRowClick(r)}
                        style={{ accentColor: "#7B5AC4" }}
                      />
                    </td>
                  )}
                  <td style={td}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        background: avatarColor(r.candidateId),
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      {initials(r.name)}
                    </div>
                  </td>
                  <td style={{ ...td, fontWeight: 600, color: "#0F172A" }}>
                    {r.name}
                  </td>
                  <td style={{ ...td, color: "#475569" }}>{r.position}</td>
                  <td style={td}>
                    {(() => {
                      const cached = scoreCacheRef.current.get(r.id);
                      if (cached?.status === "loading")
                        return (
                          <span style={{ color: "#CBD5E1", fontSize: 11 }}>
                            …
                          </span>
                        );
                      const score = cached?.score ?? r.score;
                      return score == null ? (
                        <span style={{ color: "#CBD5E1", fontSize: 12 }}>
                          —
                        </span>
                      ) : (
                        <ScoreRing score={score} size={36} />
                      );
                    })()}
                  </td>
                  <td style={td}>
                    {(() => {
                      const cached = scoreCacheRef.current.get(r.id);
                      if (cached?.status === "loading")
                        return (
                          <span style={{ color: "#CBD5E1", fontSize: 11 }}>
                            …
                          </span>
                        );
                      const rec = cached?.recommendation ?? r.recommendation;
                      // Backend now returns object {level, confidence, reasoning, factors}.
                      // Legacy callers may have passed a string label — fall back to score-derive.
                      const recScore = cached?.score ?? r.score;
                      const isObj = rec && typeof rec === "object";
                      if (!isObj && !recScore)
                        return (
                          <span style={{ color: "#CBD5E1", fontSize: 12 }}>
                            —
                          </span>
                        );
                      return (
                        <RecommendationBadge
                          level={isObj ? rec.level : undefined}
                          confidence={isObj ? rec.confidence : undefined}
                          reasoning={isObj ? rec.reasoning : undefined}
                          score={!isObj ? recScore : undefined}
                          size="sm"
                        />
                      );
                    })()}
                  </td>
                  <td style={td}>{STATUS_TAG(r.status)}</td>
                  <td style={td} onClick={(e) => e.stopPropagation()}>
                    {String(r.status).toUpperCase() === "PENDING" ? (
                      <button
                        onClick={(e) => sendReminder(r, e)}
                        disabled={remindState[r.id] === "sending"}
                        style={{
                          padding: "5px 12px",
                          fontSize: 11,
                          fontWeight: 600,
                          borderRadius: 6,
                          border: "1px solid #C7D2FE",
                          background:
                            remindState[r.id] === "sent"
                              ? "#ECFDF5"
                              : remindState[r.id] === "failed"
                                ? "#FEF2F2"
                                : "#EEF2FF",
                          color:
                            remindState[r.id] === "sent"
                              ? "#047857"
                              : remindState[r.id] === "failed"
                                ? "#B91C1C"
                                : "#4338CA",
                          cursor:
                            remindState[r.id] === "sending"
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {remindState[r.id] === "sending"
                          ? "Sending…"
                          : remindState[r.id] === "sent"
                            ? "Reminded"
                            : remindState[r.id] === "failed"
                              ? "Retry"
                              : "Remind"}
                      </button>
                    ) : isCompleted(r.status) ? (
                      <div style={{ display: "inline-flex", gap: 6 }}>
                        <button
                          onClick={(e) => openReport(r, e)}
                          aria-label="View results"
                          title="View results"
                          style={iconBtn}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#EEF2FF";
                            e.currentTarget.style.color = "#4338CA";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "white";
                            e.currentTarget.style.color = "#475569";
                          }}
                        >
                          <EyeIcon />
                        </button>
                        <button
                          onClick={(e) => downloadReport(r, e)}
                          disabled={actionState[r.id] === "downloading"}
                          aria-label="Download report"
                          title="Download report"
                          style={{
                            ...iconBtn,
                            cursor:
                              actionState[r.id] === "downloading"
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              actionState[r.id] === "downloading" ? 0.5 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (actionState[r.id] === "downloading") return;
                            e.currentTarget.style.background = "#EEF2FF";
                            e.currentTarget.style.color = "#4338CA";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "white";
                            e.currentTarget.style.color = "#475569";
                          }}
                        >
                          <DownloadIcon />
                        </button>
                      </div>
                    ) : (
                      <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </motion.div>
    </div>
  );
};

const iconBtn = {
  width: 30,
  height: 30,
  borderRadius: 6,
  border: "1px solid #E2E8F0",
  background: "white",
  color: "#475569",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background 0.15s, color 0.15s",
};

const hdrRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  marginBottom: 16,
  gap: 16,
};
const hdrTitle = { fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 };
const hdrSub = { fontSize: 13, color: "#64748B", margin: "4px 0 0" };
const btnPrimary = {
  padding: "8px 16px",
  borderRadius: 6,
  background: "#7B5AC4",
  color: "white",
  border: "none",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
const btnGhost = {
  padding: "8px 14px",
  borderRadius: 6,
  background: "white",
  color: "#475569",
  border: "1px solid #E2E8F0",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
const activeBtn = {
  borderColor: "#7B5AC4",
  color: "#4338CA",
  background: "#EEF2FF",
};
const th = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: 11,
  color: "#64748B",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  fontWeight: 600,
  borderBottom: "1px solid #E2E8F0",
};
const td = { padding: "10px 14px", verticalAlign: "middle" };

export default Interviews;
